import React from 'react';
import PropTypes from 'prop-types';
/* eslint-enable */

import styled from 'styled-components';
import { BaseLink, BaseLinkStyles, Paragraph } from 'aether-marketing';
import AspectRatioPluginV6 from './AspectRatioImagePluginV6';
import CardWrapperV6 from './CardWrapperV6';
import Button from '../v5_buttons/Button';

/* eslint-disable import/no-extraneous-dependencies */
const pmUuid = require('@postman/uuid');
/* eslint-enable */

// Styled components for card below

const CardBody = styled.div`
  padding: 2.4rem 2.4rem 2rem 2.4rem;
`;

const Banner = styled.div`
  background-color: ${(props) => props.theme.colors.grey_00};
  border-radius: ${(props) => props.theme.borderRadius.small};
  box-shadow: ${(props) => props.theme.shadows.card_shadow};
  font-size: 1.2rem;
  line-height: 1.2;
  position: absolute;
  text-align: center;
  top: 8px;
  right: 8px;
  text-transform: uppercase;
  padding: 0.8rem;

  .header {
    font-size: 2.4rem;
    font-family: ${(props) => props.theme.fonts.Inter};
    font-weight: 600;
    margin-bottom: 4px;
    line-height: 1;
    color: ${(props) => props.theme.colors.orange_50};
  }
  .subheader {
    font-size: 1.2rem;
    margin-bottom: 0px;
    letter-spacing: 0.05em;
    line-height: 1;
    color: ${(props) => props.theme.colors.orange_50};
  }

  @media (max-width: 576px) {
    right: 8px;
  }
`;

const CardBodyParagraph = styled.div`
  a {
    ${BaseLinkStyles.componentStyle.rules}
  }
`;

const Tag = styled.span`
  border: 1px solid ${(props) => props.theme.colors.grey_30};
  border-radius: ${(props) => props.theme.borderRadius.small};
  color: ${(props) => props.theme.colors.grey_50};
  display: inline-block;
  font-size: 1.2rem;
  line-height: 1;
  margin-right: 8px;
  margin-bottom: 8px;
  padding: 0.5rem 0.8rem;
  text-transform: uppercase;
  white-space: nowrap;
`;

// Component recipe for card
// Supports optional image, title, body & link
function CardBasicV6({
  col, // bootstrap classes for col width
  textPadding,
  textAlign,
  tags,
  header, // title object
  subtitle, // subtitle
  body, // main copy
  link, // Link object
  eyebrow,
  banner,
  media, // image object
  hover, // boolean for hover affect
  clickableCardLink, // boolean, makes entire card clickable
  className,
  fullWidthImage, // boolean, enables padding for entire card
  altBoxShadow,
  event,
  eventProp,
  roundedCorners // If rounded corners is false, no border radius on CardWrapperV6
}) {
  return (
    <div
      data-event={event}
      data-event-prop={eventProp}
      className={`${col || 'col-lg-6 col-xl-4'} ${className}`}
      key={pmUuid()}
    >
      <CardWrapperV6
        altBoxShadow={altBoxShadow}
        hover={hover}
        href={clickableCardLink ? link.src : null}
        gaCategory={clickableCardLink ? link.gaCategory : null}
        gaLabel={clickableCardLink ? link.gaLabel : null}
        className={`h-100 d-flex flex-column ${!roundedCorners ? '' : ''}`}
        fullWidthImage={fullWidthImage}
      >
        {/* Media Container, rendered as AspectRatioImagePluginV6 */}
        {media && media.src && (
          <div style={{ position: 'relative' }}>
            {banner && banner.header && banner.subheader && (
              <Banner>
                <p className="header">{banner.header}</p>
                <p className="subheader">{banner.subheader}</p>
              </Banner>
            )}
            <AspectRatioPluginV6
              src={media.src}
              role="img"
              ratio={media.ratio ? media.ratio : '1/1'}
              aria-label={media.alt || ''}
              height={media.height ? media.height : ''}
              marginBottom={media.marginBottom ? media.marginBottom : 0}
              borderRadius={media.borderRadius}
            />
          </div>
        )}
        {/* Body Container,, can accept any kind of content (p, headers or ol/li) */}
        <CardBody
          style={{ padding: textPadding && textPadding }}
          className={`${textAlign || 'left-center'}`}
        >
          {/* Tags */}
          {tags && (
            <p className="mb-3">
              {tags.map((tag) => {
                return <Tag key={pmUuid()}>{tag.text}</Tag>;
              })}
            </p>
          )}
          {eyebrow && eyebrow.text && (
            <p className="mb-2 small">
              <strong>{eyebrow.text}</strong>
            </p>
          )}
          {header && header.text && (
            <h1
              tag={header.tag ? header.tag : 'h3'}
              className={`h4 ${header.className ? header.className : ''}`}
            >
              {header.text}
            </h1>
          )}
          {subtitle && subtitle.text && (
            <Paragraph className="mb-0">{subtitle.text}</Paragraph>
          )}
          {/* Dangerously Set to make sure we render any unicode symbols like m dash.  Do not include opening and closing tag in body.text string */}
          {body && body.text && (
            <CardBodyParagraph
              className={body.className ? body.className : 'mb-3'}
              alternative={false}
              dangerouslySetInnerHTML={{ __html: body.text.join('') }}
              style={body.style && body.style}
            />
          )}
        </CardBody>
        {link.text && link.type !== 'primary' && (
          <div
            style={{ padding: '0 2.4rem 2.4rem 2.4rem' }}
            className={`${textAlign || 'text-left'} mt-auto`}
          >
            {/* If Card Container is set to be a clickable link.  Render the below link AS a P tag, so as not to nest A tags. */}
            <BaseLink
              className={clickableCardLink ? 'stretched-link' : null}
              as={clickableCardLink ? 'span' : 'a'}
              src={clickableCardLink ? null : link.src}
              target={link.target && clickableCardLink ? null : link.target}
              linkType="arrowLink"
              id={link.id ? link.id : null}
              gaLabel={link.gaLabel ? link.gaLabel : null}
              gaCategory={link.gaCategory ? link.gaCategory : null}
              event={event}
              eventProp={eventProp}
            >
              {link.text}
            </BaseLink>
          </div>
        )}
        {link.text && link.type === 'primary' && (
          <Button
            buttonType="primary"
            href={link.src}
            target={link.target}
            id={link.id ? link.id : null}
            gaLabel={link.gaLabel ? link.gaLabel : null}
            gaCategory={link.gaCategory ? link.gaCategory : null}
            event={event}
            eventProp={eventProp}
          >
            {link.text}
          </Button>
        )}
      </CardWrapperV6>
    </div>
  );
}

CardBasicV6.propTypes = {
  event: PropTypes.string,
  eventProp: PropTypes.string,
  col: PropTypes.string,
  link: PropTypes.shape({
    src: PropTypes.string,
    target: PropTypes.string,
    text: PropTypes.string,
    type: PropTypes.string,
    id: PropTypes.string,
    gaLabel: PropTypes.string,
    gaCategory: PropTypes.string,
    padding: PropTypes.string
  }),
  header: PropTypes.shape({
    text: PropTypes.string,
    tag: PropTypes.string,
    className: PropTypes.string
  }),
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string
    })
  ),
  subtitle: PropTypes.shape({
    text: PropTypes.string
  }),
  body: PropTypes.shape({
    text: PropTypes.arrayOf(PropTypes.string),
    className: PropTypes.string,
    style: PropTypes.shape({})
  }),
  media: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
    ratio: PropTypes.string,
    height: PropTypes.string,
    marginBottom: PropTypes.number,
    borderRadius: PropTypes.number
  }),
  hover: PropTypes.bool,
  clickableCardLink: PropTypes.bool,
  className: PropTypes.string,
  fullWidthImage: PropTypes.bool,
  roundedCorners: PropTypes.bool || PropTypes.string,
  altBoxShadow: PropTypes.bool,
  textPadding: PropTypes.string,
  textAlign: PropTypes.string,
  eyebrow: PropTypes.shape({
    text: PropTypes.string
  }),
  banner: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({})])
};

CardBasicV6.defaultProps = {
  event: null,
  eventProp: null,
  link: {
    text: null,
    src: null,
    target: null,
    type: null
  },
  header: {
    text: null,
    tag: null,
    className: null
  },
  tags: null,
  subtitle: {
    text: null
  },
  body: {
    text: null,
    className: null,
    style: null
  },
  media: {
    src: null,
    alt: null,
    ratio: null,
    height: '150px',
    marginBottom: null,
    borderRadius: 0
  },
  textPadding: null,
  textAlign: null,
  eyebrow: {
    text: null
  },
  banner: {
    header: null,
    subheader: null
  },
  col: null,
  hover: false,
  clickableCardLink: false,
  className: '',
  fullWidthImage: false,
  roundedCorners: false,
  altBoxShadow: false
};

export default CardBasicV6;
